/**
 * Where an answer goes when the intake is accepted: a profile field or
 * history (`model_path`, or a template question whose id is the path), the
 * vault for uploads, or nowhere. The list of targets comes from the backend;
 * labels fall back to the path itself so a missing list never blanks the UI.
 */
import { useEffect, useState } from "react";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type {
  OutgoingIntakeFormNode,
  OutgoingIntakeSaveTarget
} from "@/api/types/outgoingIntake";

const GROUP_LABEL: Record<string, string> = {
  foreign_national: "Profile",
  employment_history: "Employment",
  education_history: "Education",
  address_history: "Address history",
  immigration_status_history: "Immigration",
  immigration_entry_exit_history: "Travel",
  dependents: "Family"
};

const humanize = (segment: string): string => {
  const words = segment.replace(/[._]+/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : segment;
};

/** "foreign_national.marital_status" -> "Profile · Marital status". */
export const fallbackTargetLabel = (path: string): string => {
  const [head, ...rest] = path.split(/\[\]\.|\./);
  const group = GROUP_LABEL[head] ?? humanize(head);
  const field = rest.length ? humanize(rest.join(" ")) : "";
  return field ? `${group} · ${field}` : group;
};

/** The record path an answer is written to, or null when nothing maps it. */
export const recordPathOf = (node: OutgoingIntakeFormNode): string | null => {
  const explicit = String(node.model_path ?? "").trim();
  if (explicit) return explicit;
  const id = String(node.node_id ?? "");
  if (id.startsWith("foreign_national.") || id.includes("[]."))
    return id.split("|")[0];
  return null;
};

export type SavesTo =
  | { kind: "record"; path: string; label: string }
  | { kind: "vault"; label: string }
  | { kind: "none" };

export const savesToOf = (
  node: OutgoingIntakeFormNode,
  targets: OutgoingIntakeSaveTarget[],
  documentLabel?: string
): SavesTo => {
  if (node.question_type === "file")
    return {
      kind: "vault",
      label: `Vault · ${
        documentLabel || node.title || node.prompt || "Document"
      }`
    };
  const path = recordPathOf(node);
  if (!path) return { kind: "none" };
  const target = targets.find((t) => t.model_path === path);
  return {
    kind: "record",
    path,
    label: target?.label ?? fallbackTargetLabel(path)
  };
};

/** Targets that can hold an answer of this type. */
export const compatibleTargets = (
  targets: OutgoingIntakeSaveTarget[],
  questionType: OutgoingIntakeFormNode["question_type"]
): OutgoingIntakeSaveTarget[] =>
  targets.filter(
    (t) =>
      !questionType ||
      !t.question_types?.length ||
      t.question_types.includes(questionType)
  );

let cache: OutgoingIntakeSaveTarget[] | null = null;
let inflight: Promise<OutgoingIntakeSaveTarget[]> | null = null;

const loadTargets = (): Promise<OutgoingIntakeSaveTarget[]> => {
  if (cache) return Promise.resolve(cache);
  inflight ??= OutgoingIntakesAPI.listSaveTargets()
    .then((targets) => {
      cache = targets;
      return targets;
    })
    .catch(() => {
      inflight = null;
      return [];
    });
  return inflight;
};

/** The save-target catalog, fetched once per session. */
export const useSaveTargets = (): OutgoingIntakeSaveTarget[] => {
  const [targets, setTargets] = useState<OutgoingIntakeSaveTarget[]>(
    cache ?? []
  );
  useEffect(() => {
    let alive = true;
    void loadTargets().then((t) => alive && setTargets(t));
    return () => {
      alive = false;
    };
  }, []);
  return targets;
};
